/*
 * FICHIER: strapi.js
 * BUT: Configuration et fonctions pour communiquer avec l'API Strapi (backend)
 * 
 * Ce fichier contient :
 * - Configuration de l'URL de l'API selon l'environnement
 * - Fonctions pour récupérer les données de chaque section (actualités, événements, etc.)
 * - Transformation automatique des URLs d'images
 * - Gestion des erreurs et timeouts
 * - Fonctions pour les sondages et votes
 */

// Import d'Axios - Bibliothèque pour faire des requêtes HTTP vers l'API
import axios from 'axios';

// Configuration automatique de l'environnement
// Détecte si on est en développement local ou en production
const isDevelopment = process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost';

// OPTION : Forcer l'utilisation de l'API OVH en développement local
// Créer un fichier .env.local avec : REACT_APP_USE_PROD_API=true
// Cela permet de tester avec les données de production même en local
const FORCE_PROD_API = process.env.REACT_APP_USE_PROD_API === 'true';

// Configuration dynamique des URLs selon l'environnement
// Peut être surchargée via REACT_APP_API_URL dans .env.local
const API_URL = process.env.REACT_APP_API_URL 
  ? `${process.env.REACT_APP_API_URL}/api`
  : (isDevelopment && !FORCE_PROD_API)
    ? 'http://localhost:1337/api'
    : 'https://api.offisport.com/api';

const BASE_URL = process.env.REACT_APP_API_URL 
  ? process.env.REACT_APP_API_URL
  : (isDevelopment && !FORCE_PROD_API)
    ? 'http://localhost:1337'
    : 'https://api.offisport.com';

// Log de débogage pour vérifier la configuration (uniquement en développement)
if (isDevelopment) {
  console.log('🔧 Configuration API:', {
    isDevelopment,
    FORCE_PROD_API,
    API_URL,
    BASE_URL,
    'REACT_APP_USE_PROD_API': process.env.REACT_APP_USE_PROD_API,
    'REACT_APP_API_URL': process.env.REACT_APP_API_URL
  });
}

// Option: utiliser les médias de PROD en dev (utile si les uploads ne sont pas présents en local)
const USE_PROD_MEDIA_IN_DEV = true;
const MEDIA_BASE_URL = (isDevelopment && USE_PROD_MEDIA_IN_DEV && !FORCE_PROD_API) 
  ? 'https://api.offisport.com' 
  : BASE_URL;

// Taille de page maximale centralisée pour toutes les requêtes Strapi v5
// Note: La limite réelle est plafonnée par offisport-backend/config/api.js -> rest.defaultLimit/maxLimit
export const MAX_PAGE_SIZE = 1000; // Ajustable facilement en un seul endroit
const emptyActivitesPagination = (page, pageSize) => ({
  page: Number(page) || 1,
  pageSize: Number(pageSize) || 6,
  pageCount: 0,
  total: 0,
});

/** Une seule page Strapi (pagination serveur — une requête par page UI). */
const fetchStrapiSinglePage = async (collectionPath, buildParams, { page, pageSize }, apiClient = api) => {
  const path = collectionPath.startsWith('/') ? collectionPath : `/${collectionPath}`;
  const params = buildParams();
  params.set('pagination[page]', String(page));
  params.set('pagination[pageSize]', String(pageSize));
  params.set('pagination[withCount]', 'true');
  const response = await apiClient.get(`${path}?${params}`);
  return {
    data: response.data?.data || [],
    pagination: response.data?.meta?.pagination || emptyActivitesPagination(page, pageSize),
  };
};

// Configuration silencieuse de l'environnement
// (Cette ligne semble être un commentaire résiduel)

// Fonction utilitaire pour transformer les URLs relatives des images en URLs absolues
// Strapi retourne parfois des URLs relatives (/uploads/image.jpg) qu'il faut transformer en URLs complètes
const transformImageUrls = (data) => {
  // Si pas de données, on retourne tel quel
  if (!data) return data;
  
  // Fonction interne pour transformer une URL
  const transformUrl = (url) => {
    if (!url) return url;                          // Pas d'URL
    if (url.startsWith('http')) return url;       // Déjà absolue (http://... ou https://...)
    if (url.startsWith('/')) return MEDIA_BASE_URL + url; // Relative depuis la racine (/uploads/...)
    return url;                                    // Autre cas
  };
  
  // Traite un objet image (avec url et formats)
  const processImageObject = (obj) => {
    if (!obj) return obj;
    
    // Transforme l'URL principale de l'image
    if (obj.url) {
      obj.url = transformUrl(obj.url);
    }
    
    // Transforme les URLs des différents formats (thumbnail, small, medium, large)
    if (obj.formats) {
      Object.keys(obj.formats).forEach(format => {
        if (obj.formats[format] && obj.formats[format].url) {
          obj.formats[format].url = transformUrl(obj.formats[format].url);
        }
      });
    }
    
    return obj;
  };
  
  // Fonction récursive pour traiter toute la structure de données
  const processData = (item) => {
    // Si c'est un tableau, on traite chaque élément
    if (Array.isArray(item)) {
      return item.map(processData);
    }
    
    // Si c'est un objet, on traite ses propriétés
    if (typeof item === 'object' && item !== null) {
      const processed = { ...item }; // Copie de l'objet
      
      // Traiter les attributs Strapi v5 (structure .data.attributes)
      if (processed.attributes) {
        processed.attributes = processData(processed.attributes);
      }
      
      // Parcourt toutes les propriétés de l'objet
      Object.keys(processed).forEach(key => {
        const value = processed[key];
        
        // Si c'est un objet image avec une URL directe
        if (value && typeof value === 'object' && value.url) {
          processed[key] = processImageObject(value);
        }
        
        // Si c'est un objet avec une propriété data (relation Strapi v5)
        if (value && typeof value === 'object' && value.data) {
          processed[key] = {
            ...value,
            data: processData(value.data) // Traitement récursif
          };
        }
        
        // Traitement récursif pour les objets imbriqués (sans url ni data)
        if (value && typeof value === 'object' && !value.url && !value.data) {
          processed[key] = processData(value);
        }
      });
      
      return processed;
    }
    
    // Si ce n'est ni un tableau ni un objet, on retourne tel quel
    return item;
  };
  
  return processData(data);
};

// Configuration d'Axios avec les paramètres par défaut
const api = axios.create({
  baseURL: API_URL,        // URL de base pour toutes les requêtes
  headers: {
    'Content-Type': 'application/json', // Type de contenu JSON
  },
  timeout: 15000,          // Timeout de 15 secondes (au lieu de la valeur par défaut infinie)
  withCredentials: false   // Pas de cookies pour éviter les problèmes CORS
});

/*
 * =================================================================================
 * FONCTIONS POUR RÉCUPÉRER LES DONNÉES DES DIFFÉRENTES SECTIONS
 * =================================================================================
 * 
 * Chaque fonction fait une requête GET vers un endpoint Strapi spécifique
 * et retourne les données transformées (URLs d'images corrigées)
 */

// Récupération des actualités (legacy — préférer getActualitesPage)
export const getActualites = async () => {
  try {
    const response = await api.get(`/actualites?populate=*&pagination[page]=1&pagination[pageSize]=${MAX_PAGE_SIZE}`);
    return transformImageUrls(response.data.data);
  } catch (error) {
    console.error('Erreur lors de la récupération des actualités:', error);
    return [];
  }
};

const ACTUALITES_SORT_MAP = {
  'date-desc': ['DatePublication:desc'],
  'date-asc': ['DatePublication:asc'],
  'title-asc': ['Titre:asc'],
  'title-desc': ['Titre:desc'],
};

const buildActualitesPageQuery = ({ page, pageSize, search, category, sort }) => {
  const params = new URLSearchParams();
  params.append('fields[0]', 'Titre');
  params.append('fields[1]', 'DatePublication');
  params.append('fields[2]', 'MiseEnAvant');
  params.append('fields[3]', 'Description');
  params.append('populate[Sport][fields][0]', 'Designation');
  params.append('populate[Images][fields][0]', 'url');
  params.append('populate[Images][fields][1]', 'name');
  params.append('pagination[page]', String(page));
  params.append('pagination[pageSize]', String(pageSize));
  params.append('pagination[withCount]', 'true');
  params.append('filters[DatePublication][$lte]', new Date().toISOString());

  const sortKeys = ACTUALITES_SORT_MAP[sort] || ACTUALITES_SORT_MAP['date-desc'];
  sortKeys.forEach((s, i) => params.append(`sort[${i}]`, s));

  const term = (search || '').trim();
  if (term) {
    params.append('filters[$or][0][Titre][$containsi]', term);
    params.append('filters[$or][1][Sport][Designation][$containsi]', term);
  }

  if (category && category !== 'Tous') {
    params.append('filters[Sport][Designation][$eq]', category);
  }

  return params.toString();
};

/** Une page d'actualités (liste légère) + pagination Strapi. */
export const getActualitesPage = async ({
  page = 1,
  pageSize = 6,
  search = '',
  category = 'Tous',
  sort = 'date-desc',
} = {}) => {
  const emptyPagination = { page: 1, pageSize, pageCount: 0, total: 0 };
  try {
    const query = buildActualitesPageQuery({ page, pageSize, search, category, sort });
    const response = await api.get(`/actualites?${query}`);
    const items = transformImageUrls(response.data?.data || []);
    const pagination = response.data?.meta?.pagination || emptyPagination;
    return { items, pagination };
  } catch (error) {
    console.error('Erreur lors de la récupération paginée des actualités:', error);
    return { items: [], pagination: emptyPagination };
  }
};

/** Détail complet d'une actualité (Description, images, etc.). */
export const getActualiteDetail = async (idOrDocumentId) => {
  if (!idOrDocumentId) return null;
  try {
    const response = await api.get(`/actualites/${encodeURIComponent(idOrDocumentId)}?populate=*`);
    const data = response.data?.data;
    return data ? transformImageUrls(data) : null;
  } catch (error) {
    console.error(`Erreur lors de la récupération de l'actualité ${idOrDocumentId}:`, error);
    return null;
  }
};

// Récupération des événements
export const getEvenements = async () => {
  try {
    // Récupération explicite de tous les champs de la collection Evenement
    const response = await api.get(`/evenements?populate=*&pagination[page]=1&pagination[pageSize]=${MAX_PAGE_SIZE}`);
    return transformImageUrls(response.data.data);
  } catch (error) {
    console.error('Erreur lors de la récupération des événements:', error);
    return [];
  }
};

// Récupération des clubs
export const getClubs = async () => {
  try {
    const response = await api.get(`/clubs?populate=*&pagination[page]=1&pagination[pageSize]=${MAX_PAGE_SIZE}`);
    
    // Dans Strapi v5, les données sont dans response.data.data (tableau)
    const clubsData = response.data.data || [];
    
    const result = transformImageUrls(clubsData);
    return result;
  } catch (error) {
    console.error('❌ Erreur lors de la récupération des clubs:', error);
    return [];
  }
};

const appendClubListPopulate = (params) => {
  params.append('populate[Sport][fields][0]', 'Designation');
  params.append('populate[EtiquettesClub][fields][0]', 'Intitule');
  params.append('populate[EtiquettesClub][fields][1]', 'Description');
  params.append('populate[EtiquettesClub][populate][Image][fields][0]', 'url');
  params.append('populate[Image][fields][0]', 'url');
  params.append('populate[Image][fields][1]', 'name');
  params.append('populate[Image][fields][2]', 'alternativeText');
};

const CLUBS_SORT_MAP = {
  'name-asc': ['Nom:asc'],
  'name-desc': ['Nom:desc'],
  'sport-asc': ['Sport.Designation:asc', 'Nom:asc'],
  'commune-asc': ['Ville:asc', 'Nom:asc'],
};

const buildClubsPageQuery = ({ page, pageSize, search, sport, commune, sort }) => {
  const params = new URLSearchParams();
  ['Nom', 'Description', 'Ville', 'CodePostal', 'Adresse', 'Telephone', 'email', 'SiteWeb', 'Contact',
   'Facebook', 'Facebook_Nom', 'Instagram', 'Instagram_Nom', 'LinkedIn', 'LinkedIn_Nom'].forEach((f, i) => {
    params.append(`fields[${i}]`, f);
  });
  appendClubListPopulate(params);
  params.append('pagination[page]', String(page));
  params.append('pagination[pageSize]', String(pageSize));
  params.append('pagination[withCount]', 'true');

  const sortKeys = CLUBS_SORT_MAP[sort] || CLUBS_SORT_MAP['name-asc'];
  sortKeys.forEach((s, i) => params.append(`sort[${i}]`, s));

  const term = (search || '').trim();
  if (term) {
    params.append('filters[$or][0][Nom][$containsi]', term);
    params.append('filters[$or][1][Ville][$containsi]', term);
    params.append('filters[$or][2][Sport][Designation][$containsi]', term);
  }
  if (sport) params.append('filters[Sport][Designation][$eq]', sport);
  if (commune) params.append('filters[Ville][$containsi]', commune);

  return params.toString();
};

export const getClubsPage = async ({
  page = 1,
  pageSize = 6,
  search = '',
  sport = '',
  commune = '',
  sort = 'name-asc',
} = {}) => {
  const emptyPagination = { page: 1, pageSize, pageCount: 0, total: 0 };
  try {
    const query = buildClubsPageQuery({ page, pageSize, search, sport, commune, sort });
    const response = await api.get(`/clubs?${query}`);
    const items = transformImageUrls(response.data?.data || []);
    return { items, pagination: response.data?.meta?.pagination || emptyPagination };
  } catch (error) {
    console.error('Erreur getClubsPage:', error);
    return { items: [], pagination: emptyPagination };
  }
};

/** Liste légère (Description texte seul, sans populate lourd) pour tri aléatoire et communes. */
export const getClubsLight = async () => {
  try {
    const params = new URLSearchParams();
    ['Nom', 'Description', 'Ville', 'CodePostal', 'Adresse', 'Telephone', 'email', 'SiteWeb', 'Contact',
     'Facebook', 'Facebook_Nom', 'Instagram', 'Instagram_Nom', 'LinkedIn', 'LinkedIn_Nom'].forEach((f, i) => {
      params.append(`fields[${i}]`, f);
    });
    appendClubListPopulate(params);
    params.append('pagination[page]', '1');
    params.append('pagination[pageSize]', String(MAX_PAGE_SIZE));
    const response = await api.get(`/clubs?${params}`);
    return transformImageUrls(response.data?.data || []);
  } catch (error) {
    console.error('Erreur getClubsLight:', error);
    return [];
  }
};

export const getClubDetail = async (idOrDocumentId) => {
  if (!idOrDocumentId) return null;
  try {
    const response = await api.get(`/clubs/${encodeURIComponent(idOrDocumentId)}?populate=*`);
    const data = response.data?.data;
    return data ? transformImageUrls(data) : null;
  } catch (error) {
    console.error(`Erreur getClubDetail ${idOrDocumentId}:`, error);
    return null;
  }
};

export const getClubsByIds = async (ids = []) => {
  const unique = [...new Set(ids.filter(Boolean).map(String))];
  if (!unique.length) return [];
  try {
    const params = new URLSearchParams();
    appendClubListPopulate(params);
    params.append('pagination[pageSize]', String(Math.min(unique.length, MAX_PAGE_SIZE)));
    unique.forEach((id, i) => {
      params.append(`filters[$or][${i}][documentId][$eq]`, id);
      params.append(`filters[$or][${unique.length + i}][id][$eq]`, id);
    });
    const response = await api.get(`/clubs?${params}`);
    return transformImageUrls(response.data?.data || []);
  } catch (error) {
    console.error('Erreur getClubsByIds:', error);
    return [];
  }
};

const appendEvenementListPopulate = (params) => {
  params.append('populate[Sport][fields][0]', 'Designation');
  params.append('populate[image][fields][0]', 'url');
  params.append('populate[image][fields][1]', 'name');
};

const EVENEMENTS_SORT_MAP = {
  'date-asc': ['Date_debut:asc'],
  'date-desc': ['Date_debut:desc'],
  'title-asc': ['Titre:asc'],
  'title-desc': ['Titre:desc'],
};

/** Événements non terminés : Date_fin >= aujourd'hui, ou (sans Date_fin) Date_debut >= aujourd'hui. */
const appendEvenementsNotFinishedFilter = (params, andRef = { idx: 0 }) => {
  const today = new Date().toISOString().split('T')[0];
  const i = andRef.idx++;
  params.append(`filters[$and][${i}][$or][0][Date_fin][$gte]`, today);
  params.append(`filters[$and][${i}][$or][1][$and][0][Date_fin][$null]`, 'true');
  params.append(`filters[$and][${i}][$or][1][$and][1][Date_debut][$gte]`, today);
  return andRef;
};

const buildEvenementsPageQuery = ({ page, pageSize, search, category, period, sort }) => {
  const params = new URLSearchParams();
  const andRef = { idx: 0 };
  ['Titre', 'Date_debut', 'Date_fin', 'Heure_debut', 'Heure_fin', 'Emplacement', 'Ville', 'CodePostal'].forEach((f, i) => {
    params.append(`fields[${i}]`, f);
  });
  appendEvenementListPopulate(params);
  params.append('pagination[page]', String(page));
  params.append('pagination[pageSize]', String(pageSize));
  params.append('pagination[withCount]', 'true');
  appendEvenementsNotFinishedFilter(params, andRef);

  const sortKeys = EVENEMENTS_SORT_MAP[sort] || EVENEMENTS_SORT_MAP['date-asc'];
  sortKeys.forEach((s, i) => params.append(`sort[${i}]`, s));

  const term = (search || '').trim();
  if (term) {
    params.append('filters[$or][0][Titre][$containsi]', term);
    params.append('filters[$or][1][Ville][$containsi]', term);
    params.append('filters[$or][2][Emplacement][$containsi]', term);
    params.append('filters[$or][3][Sport][Designation][$containsi]', term);
  }
  if (category && category !== 'Tous') {
    params.append('filters[Sport][Designation][$containsi]', category);
  }
  if (period && period !== 'Tous' && /^\d{4}-\d{2}$/.test(period)) {
    const [y, m] = period.split('-').map(Number);
    const start = `${period}-01`;
    const lastDay = new Date(y, m, 0).getDate();
    const end = `${period}-${String(lastDay).padStart(2, '0')}`;
    const pi = andRef.idx++;
    params.append(`filters[$and][${pi}][Date_debut][$lte]`, end);
    params.append(`filters[$and][${pi}][$or][0][Date_fin][$gte]`, start);
    params.append(`filters[$and][${pi}][$or][1][$and][0][Date_fin][$null]`, 'true');
    params.append(`filters[$and][${pi}][$or][1][$and][1][Date_debut][$gte]`, start);
  }

  return params.toString();
};

export const getEvenementsPage = async ({
  page = 1,
  pageSize = 6,
  search = '',
  category = 'Tous',
  period = 'Tous',
  sort = 'date-asc',
} = {}) => {
  const emptyPagination = { page: 1, pageSize, pageCount: 0, total: 0 };
  try {
    const query = buildEvenementsPageQuery({ page, pageSize, search, category, period, sort });
    const response = await api.get(`/evenements?${query}`);
    const items = transformImageUrls(response.data?.data || []);
    return { items, pagination: response.data?.meta?.pagination || emptyPagination };
  } catch (error) {
    console.error('Erreur getEvenementsPage:', error);
    return { items: [], pagination: emptyPagination };
  }
};

export const getEvenementDetail = async (idOrDocumentId) => {
  if (!idOrDocumentId) return null;
  try {
    const response = await api.get(`/evenements/${encodeURIComponent(idOrDocumentId)}?populate=*`);
    const data = response.data?.data;
    return data ? transformImageUrls(data) : null;
  } catch (error) {
    console.error(`Erreur getEvenementDetail ${idOrDocumentId}:`, error);
    return null;
  }
};

/** Événements d'un mois (calendrier) — champs légers. */
export const getEvenementsCalendarMonth = async ({ year, month }) => {
  try {
    const y = Number(year);
    const m = Number(month);
    const start = `${y}-${String(m).padStart(2, '0')}-01`;
    const lastDay = new Date(y, m, 0).getDate();
    const end = `${y}-${String(m).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
    const params = new URLSearchParams();
    ['Titre', 'Date_debut', 'Date_fin', 'Heure_debut', 'Heure_fin', 'Emplacement', 'Ville', 'CodePostal'].forEach((f, i) => {
      params.append(`fields[${i}]`, f);
    });
    appendEvenementListPopulate(params);
    params.append('pagination[pageSize]', String(MAX_PAGE_SIZE));
    const andRef = { idx: 0 };
    appendEvenementsNotFinishedFilter(params, andRef);
    const pi = andRef.idx++;
    params.append(`filters[$and][${pi}][Date_debut][$lte]`, end);
    params.append(`filters[$and][${pi}][$or][0][Date_fin][$gte]`, start);
    params.append(`filters[$and][${pi}][$or][1][$and][0][Date_fin][$null]`, 'true');
    params.append(`filters[$and][${pi}][$or][1][$and][1][Date_debut][$gte]`, start);
    params.append('sort[0]', 'Date_debut:asc');
    const response = await api.get(`/evenements?${params}`);
    return transformImageUrls(response.data?.data || []);
  } catch (error) {
    console.error('Erreur getEvenementsCalendarMonth:', error);
    return [];
  }
};

/** Dates futures pour sélecteur de mois (sans Description). */
export const getEvenementsFutureSummary = async () => {
  try {
    const params = new URLSearchParams();
    params.append('fields[0]', 'Date_debut');
    params.append('fields[1]', 'Date_fin');
    params.append('fields[2]', 'Heure_fin');
    params.append('fields[3]', 'Titre');
    params.append('pagination[pageSize]', String(MAX_PAGE_SIZE));
    appendEvenementsNotFinishedFilter(params);
    params.append('sort[0]', 'Date_debut:asc');
    const response = await api.get(`/evenements?${params}`);
    return response.data?.data || [];
  } catch (error) {
    console.error('Erreur getEvenementsFutureSummary:', error);
    return [];
  }
};

const LIVRES_SORT_MAP = {
  random: ['MiseEnAvant:desc', 'DateParution:desc'],
  newest: ['MiseEnAvant:desc', 'DateParution:desc'],
  oldest: ['MiseEnAvant:desc', 'DateParution:asc'],
  'title-asc': ['MiseEnAvant:desc', 'Titre:asc'],
  'title-desc': ['MiseEnAvant:desc', 'Titre:desc'],
};

const buildLivresPageQuery = ({ page, pageSize, search, category, availability, sort }) => {
  const params = new URLSearchParams();
  ['Titre', 'Auteur', 'Editeur', 'Disponible', 'DateParution', 'MiseEnAvant'].forEach((f, i) => {
    params.append(`fields[${i}]`, f);
  });
  params.append('populate[sport][fields][0]', 'Designation');
  params.append('populate[Image][fields][0]', 'url');
  params.append('pagination[page]', String(page));
  params.append('pagination[pageSize]', String(pageSize));
  params.append('pagination[withCount]', 'true');

  const sortKeys = LIVRES_SORT_MAP[sort] || LIVRES_SORT_MAP.newest;
  sortKeys.forEach((s, i) => params.append(`sort[${i}]`, s));

  const term = (search || '').trim();
  if (term) {
    params.append('filters[$or][0][Titre][$containsi]', term);
    params.append('filters[$or][1][Auteur][$containsi]', term);
    params.append('filters[$or][2][Editeur][$containsi]', term);
  }
  if (category && category !== 'Tous') {
    params.append('filters[sport][Designation][$eq]', category);
  }
  if (availability === 'Disponibles') params.append('filters[Disponible][$eq]', 'true');
  if (availability === 'Empruntés') params.append('filters[Disponible][$eq]', 'false');

  return params.toString();
};

export const getLivresPage = async ({
  page = 1,
  pageSize = 6,
  search = '',
  category = 'Tous',
  availability = 'Tous',
  sort = 'newest',
} = {}) => {
  const emptyPagination = { page: 1, pageSize, pageCount: 0, total: 0 };
  try {
    const query = buildLivresPageQuery({ page, pageSize, search, category, availability, sort });
    const response = await api.get(`/livres?${query}`);
    const items = transformImageUrls(response.data?.data || []);
    return { items, pagination: response.data?.meta?.pagination || emptyPagination };
  } catch (error) {
    console.error('Erreur getLivresPage:', error);
    return { items: [], pagination: emptyPagination };
  }
};

export const getLivresLight = async () => {
  try {
    const params = new URLSearchParams();
    ['Titre', 'Auteur', 'Editeur', 'Disponible', 'DateParution', 'MiseEnAvant'].forEach((f, i) => {
      params.append(`fields[${i}]`, f);
    });
    params.append('populate[sport][fields][0]', 'Designation');
    params.append('populate[Image][fields][0]', 'url');
    params.append('pagination[pageSize]', String(MAX_PAGE_SIZE));
    const response = await api.get(`/livres?${params}`);
    return transformImageUrls(response.data?.data || []);
  } catch (error) {
    console.error('Erreur getLivresLight:', error);
    return [];
  }
};

export const getLivreDetail = async (idOrDocumentId) => {
  if (!idOrDocumentId) return null;
  try {
    const response = await api.get(`/livres/${encodeURIComponent(idOrDocumentId)}?populate=*`);
    const data = response.data?.data;
    return data ? transformImageUrls(data) : null;
  } catch (error) {
    console.error(`Erreur getLivreDetail ${idOrDocumentId}:`, error);
    return null;
  }
};

const PARTENAIRES_SORT_MAP = {
  'name-asc': ['Nom:asc'],
  'name-desc': ['Nom:desc'],
  'type-asc': ['Type:asc', 'Nom:asc'],
  'commune-asc': ['Ville:asc', 'Nom:asc'],
};

const buildPartenairesPageQuery = ({ page, pageSize, search, type, commune, sort }) => {
  const params = new URLSearchParams();
  ['Nom', 'Type', 'Adresse', 'Telephone', 'Mail', 'SiteWeb', 'Ville', 'CodePostal',
   'Facebook', 'Facebook_Nom', 'Instagram', 'Instagram_Nom', 'LinkedIn', 'LinkedIn_Nom'].forEach((f, i) => {
    params.append(`fields[${i}]`, f);
  });
  params.append('populate[Logo][fields][0]', 'url');
  params.append('pagination[page]', String(page));
  params.append('pagination[pageSize]', String(pageSize));
  params.append('pagination[withCount]', 'true');

  const sortKeys = PARTENAIRES_SORT_MAP[sort] || PARTENAIRES_SORT_MAP['name-asc'];
  sortKeys.forEach((s, i) => params.append(`sort[${i}]`, s));

  const term = (search || '').trim();
  if (term) {
    params.append('filters[$or][0][Nom][$containsi]', term);
    params.append('filters[$or][1][Ville][$containsi]', term);
  }
  if (type && type !== 'Tous') params.append('filters[Type][$eq]', type);
  if (commune) params.append('filters[Ville][$containsi]', commune);

  return params.toString();
};

export const getPartenairesPage = async ({
  page = 1,
  pageSize = 6,
  search = '',
  type = 'Tous',
  commune = '',
  sort = 'name-asc',
} = {}) => {
  const emptyPagination = { page: 1, pageSize, pageCount: 0, total: 0 };
  try {
    const query = buildPartenairesPageQuery({ page, pageSize, search, type, commune, sort });
    const response = await api.get(`/partenaires?${query}`);
    const items = transformImageUrls(response.data?.data || []);
    return { items, pagination: response.data?.meta?.pagination || emptyPagination };
  } catch (error) {
    console.error('Erreur getPartenairesPage:', error);
    return { items: [], pagination: emptyPagination };
  }
};

export const getPartenaireDetail = async (idOrDocumentId) => {
  if (!idOrDocumentId) return null;
  try {
    const response = await api.get(`/partenaires/${encodeURIComponent(idOrDocumentId)}?populate=*`);
    const data = response.data?.data;
    return data ? transformImageUrls(data) : null;
  } catch (error) {
    console.error(`Erreur getPartenaireDetail ${idOrDocumentId}:`, error);
    return null;
  }
};

export const getPartenairesLight = async () => {
  const { items } = await getPartenairesPage({
    page: 1,
    pageSize: MAX_PAGE_SIZE,
    sort: 'name-asc',
  });
  return items;
};

const pickClubLogoUrl = (club) => {
  if (!club?.Image) return null;
  const imageData = Array.isArray(club.Image) ? club.Image[0] : club.Image;
  const url = imageData?.url;
  if (!url) return null;
  return url.startsWith('/') ? `${MEDIA_BASE_URL}${url}` : url;
};

const pickClubSportName = (club) => {
  if (!club?.Sport) return 'Sport non spécifié';
  if (typeof club.Sport === 'string') return club.Sport;
  return club.Sport.Designation || club.Sport.data?.Designation || 'Sport non spécifié';
};

const groupLabel5AByYear = (label5aData) => {
  const groupedByYear = (label5aData || []).reduce((acc, item) => {
    const annee = item.Annee;
    const type = item.Type;
    const club = item.club;
    if (!acc[annee]) {
      acc[annee] = {
        id: `label5a-${annee}`,
        annee,
        description: `Labels 5A de l'année ${annee}`,
        clubs: { OR: [], ARGENT: [], BRONZE: [] },
      };
    }
    if (club && type) {
      acc[annee].clubs[type].push({
        id: club.id,
        nom: club.Nom || 'Club sans nom',
        sport: pickClubSportName(club),
        logo: pickClubLogoUrl(club),
        adresse: club.Adresse || null,
        niveau: type.toLowerCase(),
      });
    }
    return acc;
  }, {});
  return Object.values(groupedByYear).sort((a, b) => b.annee - a.annee);
};

const buildLabel5APageParams = () => {
  const params = new URLSearchParams();
  params.append('fields[0]', 'Annee');
  params.append('fields[1]', 'Type');
  params.append('populate[club][fields][0]', 'Nom');
  params.append('populate[club][fields][1]', 'Adresse');
  params.append('populate[club][populate][Sport][fields][0]', 'Designation');
  params.append('populate[club][populate][Image][fields][0]', 'url');
  params.append('sort[0]', 'Annee:desc');
  return params;
};

export const mapLabel5AGroupedToActivities = (grouped) =>
  (grouped || []).map((item) => {
    const allClubs = [
      ...(item.clubs?.OR || []).map((club) => ({ ...club, niveau: 'or' })),
      ...(item.clubs?.ARGENT || []).map((club) => ({ ...club, niveau: 'argent' })),
      ...(item.clubs?.BRONZE || []).map((club) => ({ ...club, niveau: 'bronze' })),
    ];
    return {
      id: `label5a-${item.id}`,
      title: `${item.annee}`,
      excerpt: `Découvrez les clubs récompensés par le Label 5A pour l'année ${item.annee}.`,
      category: 'Label 5A',
      date: String(item.annee),
      description: item.description,
      clubs: allClubs,
    };
  });

// Label5A : charger toutes les lignes, regrouper par année, puis paginer côté client
export const getLabel5A = async ({ page = 1, pageSize = 6, search = '' } = {}) => {
  const empty = { items: [], pagination: emptyActivitesPagination(page, pageSize) };
  try {
    const { data } = await fetchStrapiSinglePage(
      '/label-5-as',
      () => {
        const params = buildLabel5APageParams();
        const term = (search || '').trim();
        if (term) {
          params.append('filters[$or][0][club][Nom][$containsi]', term);
          params.append('filters[$or][1][Annee][$containsi]', term);
        }
        return params;
      },
      { page: 1, pageSize: MAX_PAGE_SIZE }
    );
    const allItems = mapLabel5AGroupedToActivities(
      groupLabel5AByYear(transformImageUrls(data))
    );
    const total = allItems.length;
    const pageCount = total ? Math.ceil(total / pageSize) : 0;
    const start = (page - 1) * pageSize;
    return {
      items: allItems.slice(start, start + pageSize),
      pagination: { page, pageSize, pageCount, total },
    };
  } catch (error) {
    console.error('❌ Erreur lors de la récupération des Label5A:', error);
    return empty;
  }
};

const appendClubDuMoisRelationPopulate = (params, key) => {
  params.append(`populate[${key}][fields][0]`, 'Nom');
  params.append(`populate[${key}][populate][Sport][fields][0]`, 'Designation');
  params.append(`populate[${key}][populate][Image][fields][0]`, 'url');
};

const buildClubsDuMoisDecathlonPageParams = () => {
  const params = new URLSearchParams();
  params.append('fields[0]', 'MoisAnnee');
  ['club1', 'club2', 'club3'].forEach((k) => appendClubDuMoisRelationPopulate(params, k));
  params.append('sort[0]', 'MoisAnnee:desc');
  return params;
};

// Récupération paginée des clubs du mois Décathlon (une page Strapi à la fois)
export const getClubsDuMoisDecathlon = async ({ page = 1, pageSize = 6, search = '' } = {}) => {
  const empty = { items: [], pagination: emptyActivitesPagination(page, pageSize) };
  try {
    const { data, pagination } = await fetchStrapiSinglePage(
      '/club-du-mois-decathlons',
      () => {
        const params = buildClubsDuMoisDecathlonPageParams();
        const term = (search || '').trim();
        if (term) {
          ['club1', 'club2', 'club3'].forEach((key, i) => {
            params.append(`filters[$or][${i}][${key}][Nom][$containsi]`, term);
          });
        }
        return params;
      },
      { page, pageSize }
    );
    if (!data.length) {
      console.warn('[API][CDM] Aucun élément retourné. Vérifier publication et permissions Public.');
    }
    const items = transformClubsDuMoisDecathlon(transformImageUrls(data));
    return { items, pagination };
  } catch (error) {
    console.error('❌ Erreur lors de la récupération des clubs du mois Décathlon:', error);
    return empty;
  }
};

// Fonction utilitaire pour convertir les données Strapi en format attendu par le frontend
export const transformClubsDuMoisDecathlon = (strapiData) => {
  if (!strapiData || !Array.isArray(strapiData)) {
    return [];
  }

  const now = new Date();
  
  return strapiData
    .map((item, index) => {
      // Dans Strapi v5 avec populate, les données sont directement sur l'objet, pas dans .attributes
      const date = new Date(item.MoisAnnee);
      
  
      
      // Filtre selon la mémoire: exclure les événements sans date valide ou totalement passés
      if (!item.MoisAnnee || isNaN(date.getTime())) {
        return null; // Exclure les entrées sans date valide
      }
      
      // Pas de filtre de date - affichage de tous les clubs du mois de toutes les périodes
      
      
      // Convertir la date en format lisible français
      const formatDateToFrench = (date) => {
        const months = [
          'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
          'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
        ];
        
        return `${months[date.getMonth()]} ${date.getFullYear()}`;
      };

      // Créer le podium à partir des relations
      const podium = [];
      
  
      
      // Variable pour stocker l'image du club 1er (pour la vignette)
      let club1ImageUrl = null;
      
      // Club 1 (Or)
      if (item.club1) {
        
        // Gestion sécurisée du sport (pourrait être un objet)
        let sportValue = 'Football'; // Valeur par défaut
        if (item.club1.Sport) {
          sportValue = typeof item.club1.Sport === 'string' ? item.club1.Sport : 
                      item.club1.Sport.Designation || 'Football';
        } else if (item.club1.sport) {
          sportValue = typeof item.club1.sport === 'string' ? item.club1.sport : 
                      item.club1.sport.Designation || 'Football';
        }
        
        // Debug pour voir la structure des images
        
        // Gestion des images Strapi v5 (array)
        club1ImageUrl = item.club1.Image && item.club1.Image.length > 0 
          ? item.club1.Image[0].url 
          : (item.club1.image && item.club1.image.length > 0 ? item.club1.image[0].url : null);
        
        const club1Info = {
          position: 1,
          nom: item.club1.Nom || item.club1.nom || 'Club non défini',
          sport: sportValue,
          logo: club1ImageUrl ? `${MEDIA_BASE_URL}${club1ImageUrl}` : null
        };
        podium.push(club1Info);
      } 

      // Club 2 (Argent)
      if (item.club2) {
        
        // Gestion sécurisée du sport (pourrait être un objet)
        let sportValue2 = 'Sport non défini'; // Valeur par défaut
        if (item.club2.Sport) {
          sportValue2 = typeof item.club2.Sport === 'string' ? item.club2.Sport : 
                       item.club2.Sport.Designation || 'Sport non défini';
        } else if (item.club2.sport) {
          sportValue2 = typeof item.club2.sport === 'string' ? item.club2.sport : 
                       item.club2.sport.Designation || 'Sport non défini';
        }
        
      
        // Gestion des images Strapi v5 (array)
        const club2ImageUrl = item.club2.Image && item.club2.Image.length > 0 
          ? item.club2.Image[0].url 
          : (item.club2.image && item.club2.image.length > 0 ? item.club2.image[0].url : null);
        
        const club2Info = {
          position: 2,
          nom: item.club2.Nom || item.club2.nom || 'Club non défini',
          sport: sportValue2,
          logo: club2ImageUrl ? `${MEDIA_BASE_URL}${club2ImageUrl}` : null
        };
        podium.push(club2Info);
      }

      // Club 3 (Bronze)
      if (item.club3) {
      
        
        // Gestion sécurisée du sport (pourrait être un objet)
        let sportValue3 = 'Sport non défini'; // Valeur par défaut
        if (item.club3.Sport) {
          sportValue3 = typeof item.club3.Sport === 'string' ? item.club3.Sport : 
                       item.club3.Sport.Designation || 'Sport non défini';
        } else if (item.club3.sport) {
          sportValue3 = typeof item.club3.sport === 'string' ? item.club3.sport : 
                       item.club3.sport.Designation || 'Sport non défini';
        }
        
       
        
        // Gestion des images Strapi v5 (array)
        const club3ImageUrl = item.club3.Image && item.club3.Image.length > 0 
          ? item.club3.Image[0].url 
          : (item.club3.image && item.club3.image.length > 0 ? item.club3.image[0].url : null);
        
        const club3Info = {
          position: 3,
          nom: item.club3.Nom || item.club3.nom || 'Club non défini',
          sport: sportValue3,
          logo: club3ImageUrl ? `${MEDIA_BASE_URL}${club3ImageUrl}` : null
        };
        podium.push(club3Info);
      } 

      const finalItem = {
        id: `strapi-cdm-${item.id}`, // ID unique préfixé pour éviter les conflits
        title: `📅 ${formatDateToFrench(date)}`,
        category: "Club du mois Decathlon",
        date: formatDateToFrench(date),
        author: "Décathlon Compiègne",
        excerpt: `Découvrez le podium des clubs du mois de ${formatDateToFrench(date).toLowerCase()}.`,
        image: club1ImageUrl ? `${MEDIA_BASE_URL}${club1ImageUrl}` : null, // Image du club 1er (OR) sur la vignette
        rawDate: date,
        podium: podium
      };
      
      return finalItem;
    })
    .filter((item, index) => {
      if (item === null) {
        return false;
      }
      return true;
    });
};

// Récupération des ressources de la bibliothèque
export const getBibliotheque = async () => {
  try {
    const response = await api.get(`/bibliotheques?populate=*&pagination[page]=1&pagination[pageSize]=${MAX_PAGE_SIZE}`);
    return transformImageUrls(response.data.data);
  } catch (error) {
    console.error('Erreur lors de la récupération des ressources de la bibliothèque:', error);
    return [];
  }
};

// Récupération des livres
export const getLivres = async () => {
  try {
    // Strapi v5 : syntaxe populate spécifique pour les relations
    const response = await api.get(`/livres?populate[sport]=*&populate[Image]=*&pagination[page]=1&pagination[pageSize]=${MAX_PAGE_SIZE}`);
    
    // CORRECTION : Utiliser transformImageUrls comme les autres fonctions pour corriger les URLs des images
    return transformImageUrls(response.data.data);
  } catch (error) {
    console.error('Erreur lors de la récupération des livres:', error);
    throw error;
  }
};

// Récupération des sports pour les filtres de la bibliothèque
export const getSports = async () => {
  try {
    const response = await api.get(`/sports?pagination[page]=1&pagination[pageSize]=${MAX_PAGE_SIZE}`);
    // logs nettoyés
    return response.data.data;
  } catch (error) {
    console.error('Erreur lors de la récupération des sports:', error);
    return [];
  }
};

/** Liste des étiquettes club (filtres + icônes). */
export const getEtiquettesClub = async () => {
  try {
    const params = new URLSearchParams();
    params.append('fields[0]', 'Intitule');
    params.append('fields[1]', 'Description');
    params.append('populate[Image][fields][0]', 'url');
    params.append('pagination[page]', '1');
    params.append('pagination[pageSize]', String(MAX_PAGE_SIZE));
    params.append('sort[0]', 'Intitule:asc');
    const response = await api.get(`/etiquette-clubs?${params}`);
    return transformImageUrls(response.data?.data || []);
  } catch (error) {
    console.error('Erreur lors de la récupération des étiquettes club:', error);
    return [];
  }
};

// Récupération des données du club fidélité (ancienne fonction, semble obsolète)
export const getPartenaires = async () => {
  try {
    const response = await api.get(`/club-fidelites?populate=*&pagination[page]=1&pagination[pageSize]=${MAX_PAGE_SIZE}`);
    return transformImageUrls(response.data.data);
  } catch (error) {
    console.error('Erreur lors de la récupération des données du club fidélité:', error);
    return [];
  }
};

// Récupération des partenaires (fonction principale)
export const getPartenairesData = async () => {
  try {
    const response = await api.get(`/partenaires?populate=*&pagination[page]=1&pagination[pageSize]=${MAX_PAGE_SIZE}`);
    return transformImageUrls(response.data.data);
  } catch (error) {
    console.error('Erreur lors de la récupération des partenaires:', error);
    return [];
  }
};

/**
 * Vérifie l'accès à l'application : backend joignable ET mode maintenance désactivé.
 * Un seul appel GET /accueil couvre les deux cas.
 * @returns {Promise<{backendOk: boolean, maintenance: boolean}>}
 */
export const checkAppAccess = async () => {
  try {
    const response = await api.get('/accueil?fields[0]=ModeMaintenance', { timeout: 10000 });
    const data = response.data?.data;
    return {
      backendOk: true,
      maintenance: Boolean(data?.ModeMaintenance),
    };
  } catch (error) {
    if (isDevelopment) {
      console.warn('Backend injoignable (maintenance ou erreur):', error?.message || error);
    }
    return { backendOk: false, maintenance: false };
  }
};

/**
 * Alias rétrocompatible — conservé au cas où d'autres modules l'importeraient.
 * @returns {Promise<boolean>}
 */
export const checkBackendHealth = async () => {
  return (await checkAppAccess()).backendOk;
};

// Récupération des données de la page d'accueil (logo et bandeau)
export const getAccueil = async () => {
  try {
    const response = await api.get('/accueil?populate=*');
    const result = transformImageUrls(response.data.data);
    return result;
  } catch (error) {
    console.error('Erreur lors de la récupération des données d\'accueil:', error);
    return null;
  }
};

/**
 * Fonction utilitaire pour réessayer une requête API en cas d'échec
 * Utile quand le réseau est instable ou le serveur temporairement indisponible
 * 
 * @param {Function} fn - La fonction à exécuter
 * @param {number} retries - Le nombre de tentatives (défaut: 3)
 * @param {number} delay - Le délai entre les tentatives en millisecondes (défaut: 1000ms)
 */
const retryRequest = async (fn, retries = 3, delay = 1000) => {
  try {
    return await fn();
  } catch (error) {
    if (retries <= 0) {
      throw error;
    }
    // Retry silencieux pour éviter les logs excessifs
    await new Promise(resolve => setTimeout(resolve, delay));
    return retryRequest(fn, retries - 1, delay);
  }
};

/**
 * Récupère les sondages depuis Strapi
 * @returns {Object} Objet contenant les sondages et éventuellement une erreur
 */
export const getSondages = async () => {
  try {
    const endpoint = `/sondages?populate=*&pagination[page]=1&pagination[pageSize]=${MAX_PAGE_SIZE}`;
    const response = await retryRequest(() => api.get(endpoint));
    
    if (!response || !response.data || !response.data.data) {
      return { sondages: [], error: "Format de réponse invalide" };
    }
    
    const sondages = response.data.data;
    
    // Récupérer toutes les questions puis les associer aux sondages
    try {
      const questionsResponse = await api.get(`/questions?populate=*&pagination[page]=1&pagination[pageSize]=${MAX_PAGE_SIZE}`);
      let allQuestions = [];
      
      if (questionsResponse.data && questionsResponse.data.data) {
        allQuestions = questionsResponse.data.data;
      }
      
      // Pour chaque sondage, chercher les questions correspondantes
      for (const sondage of sondages) {
        if (!sondage.attributes.questions) {
          sondage.attributes.questions = { data: [] };
        }
        
        const matchingQuestions = allQuestions.filter(question => {
          const attrs = question.attributes || {};
          
          if (attrs.AUnSondage && attrs.AUnSondage.data && attrs.AUnSondage.data.id === sondage.id) {
            return true;
          }
          
          return false;
        });
        
        if (matchingQuestions.length > 0) {
          // Récupérer les options pour chaque question
          for (const question of matchingQuestions) {
            const options = await getOptionsByQuestionId(question.id);
            if (!question.attributes.options) {
              question.attributes.options = { data: [] };
            }
            question.attributes.options.data = options;
          }
          
          sondage.attributes.questions.data = matchingQuestions;
        }
      }
      
      // Filtrer pour ne conserver que les sondages actifs
      const activeSondages = sondages.filter(sondage => {
        const statut = sondage.attributes?.statut || sondage.attributes?.Statut;
        return statut === 'actif' || statut === 'Actif';
      });
      
      return { sondages: transformImageUrls(activeSondages), error: null };
    } catch (err) {
      // Gardons seulement les erreurs critiques
    }
    
    return { sondages: transformImageUrls(sondages), error: null };
  } catch (error) {
    return { sondages: [], error: error.message };
  }
};

// Récupérer les questions pour un sondage spécifique
export const getQuestionsBySondageId = async (sondageId) => {
  try {
    const endpoint = `/questions?populate=*&filters[AUnSondage][id]=${sondageId}`;
    const response = await api.get(endpoint);
    
    if (!response.data || !response.data.data) {
      return [];
    }
    
    return response.data.data;
  } catch (error) {
    return [];
  }
};

// Récupérer les options pour une question spécifique
export const getOptionsByQuestionId = async (questionId) => {
  try {
    if (!questionId) {
      return [];
    }
    
    const parsedId = parseInt(questionId);
    if (isNaN(parsedId) || parsedId <= 0) {
      return [];
    }
    
    let options = [];
    
    try {
      const response = await api.get(`/options?populate=*&filters[ListeQuestion][id]=${parsedId}&pagination[page]=1&pagination[pageSize]=${MAX_PAGE_SIZE}`);
      
      if (response.data && response.data.data) {
        options = response.data.data;
      }
    } catch (err) {
      // Méthode alternative si ListeQuestion échoue
      try {
        const response = await api.get(`/questions/${parsedId}?populate=ListeOption`);
        
        if (response.data && response.data.data && 
            response.data.data.attributes && 
            response.data.data.attributes.ListeOption && 
            response.data.data.attributes.ListeOption.data) {
          
          options = response.data.data.attributes.ListeOption.data;
        }
      } catch (altErr) {
        // Échec silencieux
      }
    }
    
    return options;
  } catch (error) {
    return [];
  }
};

// Fonction générique pour récupérer un élément par son ID
export const getItemById = async (collection, id) => {
  try {
    const response = await api.get(`/${collection}/${id}?populate=*`);
    return response.data.data;
  } catch (error) {
    console.error(`Erreur lors de la récupération de l'élément ${collection}/${id}:`, error);
    return null;
  }
};

/**
 * Teste la connexion au serveur Strapi et affiche un message dans la console
 */
export const testStrapiConnection = async () => {
  try {
    // Tester avec un endpoint qui existe vraiment : actualites avec pagination limitée
    const response = await api.get('/actualites?pagination[limit]=1');
    return response.status === 200;
  } catch (error) {
    console.warn('Connexion Strapi échoué:', error.message);
    return false;
  }
};

/**
 * Détecte la structure du schéma Strapi pour une collection donnée
 * @param {string} collection - Le nom de la collection à inspecter
 * @returns {Object} - La structure du schéma
 */
export const detectStrapiSchema = async (collection) => {
  try {
    const itemsResponse = await api.get(`/${collection}?pagination[limit]=1`);
    
    if (itemsResponse.data && itemsResponse.data.data && itemsResponse.data.data.length > 0) {
      const sample = itemsResponse.data.data[0];
      return { detectedAttributes: Object.keys(sample.attributes || {}) };
    }
    
    return null;
  } catch (error) {
    return null;
  }
};

// Tester la connexion au démarrage - DÉSACTIVÉ pour éviter les erreurs console
// testStrapiConnection();

// submitVote et submitPollVote supprimés (dead code — /statistiques n'existe plus en Strapi v5)

/**
 * Récupère les résultats du vote pour un sondage spécifique
 * @param {number} sondageId - L'ID du sondage
 * @returns {Promise<Object>} - Les résultats du vote
 */
export const getPollResults = async (sondageId) => {
  try {
    // Récupérer les questions de ce sondage
    const questionsResponse = await api.get(`/questions?filters[AUnSondage][id]=${sondageId}&populate=*`);
    
    if (!questionsResponse.data || !questionsResponse.data.data) {
      throw new Error("Format de réponse invalide");
    }
    
    const questions = questionsResponse.data.data;
    
    // Résultats à retourner
    const results = [];
    let totalVotes = 0;
    
    // Pour chaque question, récupérer les options et leurs votes
    for (const question of questions) {
      const questionId = question.id;
      const questionText = question.attributes.texte_question || 
                          question.attributes.TexteQuestion || 
                          question.attributes.texte || 
                          question.attributes.Texte || 
                          `Question ${questionId}`;
      
      // Récupérer les options pour cette question
      const options = await getOptionsByQuestionId(questionId);
      
      // Récupérer les statistiques pour cette question
      const statsResponse = await api.get(`/statistiques?filters[question][id]=${questionId}&populate=*`);
      
      const stats = statsResponse.data?.data || [];
      
      // Pour chaque option, trouver sa statistique correspondante
      const questionResults = [];
      
      for (const option of options) {
        const optionId = option.id;
        const optionText = option.attributes?.Texte || 
                          option.attributes?.texte || 
                          option.attributes?.TexteOption || 
                          option.attributes?.texte_option ||
                          option.attributes?.libelle || 
                          `Option ${optionId}`;
        
        // Trouver la statistique pour cette option
        const optionStat = stats.find(s => s.attributes.option?.data?.id === optionId);
        const votes = optionStat ? optionStat.attributes.nombre_votes || 0 : 0;
        
        // Ajouter aux votes totaux
        totalVotes += votes;
        
        questionResults.push({
          optionId,
          text: optionText,
          votes
        });
      }
      
      // Calculer les pourcentages
      const questionTotalVotes = questionResults.reduce((sum, r) => sum + r.votes, 0);
      
      const resultsWithPercentages = questionResults.map(r => ({
        ...r,
        percentage: questionTotalVotes ? Math.round((r.votes / questionTotalVotes) * 100) : 0
      }));
      
      results.push({
        questionId,
        questionText,
        options: resultsWithPercentages,
        totalVotes: questionTotalVotes
      });
    }
    
    return { 
      success: true, 
      results: results.map(r => r.options).flat(), // Format compatible avec l'interface existante
      totalVotes 
    };
  } catch (error) {
    return { success: false, results: [], totalVotes: 0, error: error.message };
  }
};


/**
 * Vérifie si l'IP actuelle a déjà voté pour ce sondage dans les dernières 24h
 * @param {string} sondageId - ID du sondage
 * @returns {Promise<boolean>} - true si l'IP a déjà voté, false sinon
 */
export const hasIpVotedInLastDay = async (sondageId) => {
  try {
    // 1. Obtenir l'adresse IP du client
    const ipAddress = await getClientIpAddress();
    
    if (!ipAddress) {
      return false; // Si on ne peut pas déterminer l'IP, on autorise le vote
    }
    
    // 2. Vérifier en local storage si cette IP a voté récemment
    try {
      const lastVoteData = localStorage.getItem(`ip_vote_${sondageId}_${ipAddress}`);
      
      if (lastVoteData) {
        const lastVoteDate = new Date(JSON.parse(lastVoteData).timestamp);
        const currentDate = new Date();
        const diffHours = (currentDate - lastVoteDate) / (1000 * 60 * 60);
        
        // Si le dernier vote est plus récent que 24h
        if (diffHours < 24) {
          return true;
        }
      }
    } catch (error) {
      // Échec silencieux
    }
    
    // 3. En production, on ferait une vérification supplémentaire sur le serveur
    let serverCheck = false;
    try {
      // Vérification sur le serveur (simulation pour le moment)
      // const response = await api.get(`/poll-votes/check?sondageId=${sondageId}&ip=${ipAddress}`);
      // serverCheck = response.data.hasVoted;
      
      // Pour l'instant, on se base uniquement sur le localStorage
      serverCheck = false;
    } catch (error) {
      serverCheck = false; // En cas d'erreur, on autorise le vote
    }
    
    return serverCheck;
  } catch (error) {
    return false; // En cas d'erreur, on autorise le vote
  }
};

/**
 * Enregistre que l'IP actuelle a voté pour ce sondage
 * @param {string} sondageId - ID du sondage
 * @returns {Promise<boolean>} - true si l'enregistrement a réussi
 */
export const recordIpVote = async (sondageId) => {
  try {
    // 1. Obtenir l'adresse IP du client
    const ipAddress = await getClientIpAddress();
    
    if (!ipAddress) {
      return false;
    }
    
    let localStorageSuccess = false;
    
    // 2. Enregistrer en local storage
    try {
      localStorage.setItem(`ip_vote_${sondageId}_${ipAddress}`, JSON.stringify({
        timestamp: new Date().toISOString(),
        sondageId
      }));
      
      localStorageSuccess = true;
    } catch (error) {
      // Continuer avec les autres méthodes même si localStorage échoue
    }
    
    // 3. En production, on enregistrerait aussi sur le serveur
    let serverSuccess = false;
    
    try {
      // Code commenté pour l'enregistrement sur le serveur (simulation)
      // const response = await api.post(`/poll-votes/record`, {
      //   sondageId,
      //   ip: ipAddress,
      //   date: new Date().toISOString()
      // });
      // serverSuccess = response.data.success;
      
      // Pour l'instant, simulation de succès
      serverSuccess = true;
    } catch (error) {
      serverSuccess = false;
    }
    
    // Retourne vrai si au moins une des méthodes a réussi
    return localStorageSuccess || serverSuccess;
    
  } catch (error) {
    return false;
  }
};

/**
 * Obtient l'adresse IP du client
 * @returns {Promise<string|null>} - Adresse IP ou null si non disponible
 */
export const getClientIpAddress = async () => {
  try {
    // 1. Essayer d'utiliser le service ipify pour obtenir l'IP
    try {
      const response = await axios.get('https://api.ipify.org?format=json', { timeout: 3000 });
      if (response.data && response.data.ip) {
        return response.data.ip;
      }
    } catch (error) {
      console.warn("⚠️ API - Impossible d'obtenir l'IP via ipify:", error.message);
    }
    
    // 2. Alternative: utiliser un autre service (ipapi.co)
    try {
      const response = await axios.get('https://ipapi.co/json/', { timeout: 3000 });
      if (response.data && response.data.ip) {
        return response.data.ip;
      }
    } catch (error) {
      console.warn("⚠️ API - Impossible d'obtenir l'IP via ipapi.co:", error.message);
    }
    
    // 3. Aucun service disponible : retourner null (l'IP sera lue cote backend)
    return null;
  } catch (error) {
    console.error("❌ API - Erreur lors de la récupération de l'IP:", error);
    return null;
  }
};

const extractTropheePhotoUrls = (item) => {
  const urls = [];
  const pushUrl = (u) => {
    if (!u) return;
    const full = u.startsWith('http') ? u : `${MEDIA_BASE_URL}${u}`;
    if (!urls.includes(full)) urls.push(full);
  };
  const p = item.Photo || item.photo;
  if (!p) return urls;
  const list = Array.isArray(p) ? p : [];
  list.forEach((img) => {
    const attrs = img?.attributes || img;
    const mime = attrs?.mime || '';
    if (!mime || String(mime).startsWith('image/')) {
      if (attrs?.url) pushUrl(attrs.url);
    }
  });
  return urls;
};

const buildTropheeInclusSportPageParams = () => {
  const params = new URLSearchParams();
  params.append('fields[0]', 'MoisAnnee');
  params.append('populate[club][fields][0]', 'Nom');
  params.append('populate[club][populate][Sport][fields][0]', 'Designation');
  params.append('populate[club][populate][Image][fields][0]', 'url');
  params.append('populate[Photo][fields][0]', 'url');
  params.append('populate[Photo][fields][1]', 'mime');
  params.append('sort[0]', 'MoisAnnee:desc');
  return params;
};

const transformTropheeInclusSportRows = (rows) => {
  const months = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
  ];
  return (rows || []).map((item) => {
    const date = new Date(item.MoisAnnee);
    const formattedDate = `${months[date.getMonth()]} ${date.getFullYear()}`;
    const club = item.club || {};
    const clubImage = pickClubLogoUrl(club) || '';
    const sportNom = pickClubSportName(club);
    const photos = extractTropheePhotoUrls(item);
    const finalImage = photos.length > 0 ? photos[0] : clubImage;
    return {
      id: `strapi-tis-${item.id}`,
      image: finalImage,
      photos,
      title: `📅 ${formattedDate}`,
      category: 'Trophée Inclus sport Super U',
      date: formattedDate,
      author: 'Super U',
      excerpt: `Découvrez ${club.Nom || 'le club'} lauréat du Trophée Inclus sport Super U.`,
      rawDate: date,
      laureat: {
        nom: club.Nom || 'Club non spécifié',
        sport: sportNom,
        description: '',
        logo: clubImage,
      },
    };
  });
};

// Récupération paginée du Trophée Inclus Sport (une page Strapi à la fois)
export const getTropheeInclusSport = async ({ page = 1, pageSize = 6, search = '' } = {}) => {
  const empty = { items: [], pagination: emptyActivitesPagination(page, pageSize) };
  try {
    const useProductionApi = isDevelopment;
    const apiToUse = useProductionApi
      ? axios.create({
          baseURL: 'https://api.offisport.com/api',
          headers: { 'Content-Type': 'application/json' },
          timeout: 15000,
          withCredentials: false,
        })
      : api;

    const { data, pagination } = await fetchStrapiSinglePage(
      '/trophee-inclus-sports',
      () => {
        const params = buildTropheeInclusSportPageParams();
        const term = (search || '').trim();
        if (term) {
          params.append('filters[club][Nom][$containsi]', term);
        }
        return params;
      },
      { page, pageSize },
      apiToUse
    );

    const items = transformImageUrls(transformTropheeInclusSportRows(data));
    return { items, pagination };
  } catch (error) {
    console.error('❌ Erreur lors de la récupération des trophées inclus sport:', error);
    return empty;
  }
};

const buildOsarcMagPageParams = () => {
  const params = new URLSearchParams();
  params.append('fields[0]', 'DateDeParution');
  params.append('fields[1]', 'Numero');
  params.append('populate[Image][fields][0]', 'url');
  params.append('populate[PDF][fields][0]', 'url');
  params.append('sort[0]', 'DateDeParution:desc');
  return params;
};

// Récupération paginée des magazines Osarc Mag (une page Strapi à la fois)
export const getOsarcMags = async ({ page = 1, pageSize = 6, search = '' } = {}) => {
  const empty = { items: [], pagination: emptyActivitesPagination(page, pageSize) };
  try {
    const { data, pagination } = await fetchStrapiSinglePage(
      '/osarc-mags',
      () => {
        const params = buildOsarcMagPageParams();
        const term = (search || '').trim();
        if (term) {
          params.append('filters[$or][0][Numero][$containsi]', term);
        }
        return params;
      },
      { page, pageSize }
    );
    const items = transformOsarcMags(transformImageUrls(data));
    if (!items.length && page === 1) {
      console.warn('[API][OSARC] Aucun magazine retourné. Vérifier publication et permissions Public.');
    }
    return { items, pagination };
  } catch (error) {
    console.error('❌ Erreur lors de la récupération des magazines Osarc Mag:', error);
    return empty;
  }
};

// Transformation spécifique pour les magazines Osarc Mag
export const transformOsarcMags = (strapiData) => {
  if (!strapiData || !Array.isArray(strapiData)) {
    return [];
  }

  return strapiData
    .filter(mag => {
      // Filtrer les magazines avec date valide (le numéro peut être 0)
      const hasValidDate = mag.DateDeParution;
      const hasValidNumero = mag.Numero !== null && mag.Numero !== undefined;
      
      // logs nettoyés
      
      return hasValidDate && hasValidNumero;
    })
    .map(mag => {
      // logs nettoyés
      
      // Format de la date en français avec le jour
      const dateObj = new Date(mag.DateDeParution);
      const jour = dateObj.getDate();
      const mois = [
        "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
        "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"
      ][dateObj.getMonth()];
      const dateStr = `${jour} ${mois} ${dateObj.getFullYear()}`;
      
      // Créer le titre avec le numéro et la date
      const title = `OSARC Mag' N°${mag.Numero} du ${dateStr}`;
      
      // URL de l'image de la vignette - gestion robuste de la structure Strapi v5
      let imageUrl = null;
      // logs nettoyés
      
      if (mag.Image) {
        let imageData = mag.Image;
        
        // Gestion des différentes structures possibles
        if (mag.Image.data) {
          imageData = Array.isArray(mag.Image.data) ? mag.Image.data[0] : mag.Image.data;
        }
        
        // Récupérer l'URL selon la structure
        const imageAttributes = imageData.attributes || imageData;
        if (imageAttributes && imageAttributes.url) {
          imageUrl = imageAttributes.url.startsWith('http') 
            ? imageAttributes.url 
            : `${MEDIA_BASE_URL}${imageAttributes.url}`;
          // logs nettoyés
        } else {
          // logs nettoyés
        }
      } else {
        // logs nettoyés
      }
      
      // URL du PDF qui s'ouvre au clic - gestion robuste de la structure Strapi v5
      let pdfUrl = null;
      // logs nettoyés
      
      if (mag.PDF) {
        let pdfData = mag.PDF;
        
        // Gestion des différentes structures possibles
        if (mag.PDF.data) {
          pdfData = Array.isArray(mag.PDF.data) ? mag.PDF.data[0] : mag.PDF.data;
        }
        
        // Récupérer l'URL selon la structure
        const pdfAttributes = pdfData.attributes || pdfData;
        if (pdfAttributes && pdfAttributes.url) {
          pdfUrl = pdfAttributes.url.startsWith('http') 
            ? pdfAttributes.url 
            : `${MEDIA_BASE_URL}${pdfAttributes.url}`;
          // logs nettoyés
        } else {
          // logs nettoyés
        }
      } else {
        // logs nettoyés
      }
      
      const transformedMag = {
        id: `osarc-mag-${mag.id}`,
        title: title,
        category: "Osarc mag",
        date: dateStr,
        author: "OSARC",
        excerpt: `Magazine OSARC n°${mag.Numero} - ${dateStr}`,
        image: imageUrl,
        pdfUrl: pdfUrl,
        numero: mag.Numero,
        rawDate: dateObj
      };
      
      // logs nettoyés
      return transformedMag;
    })
    .sort((a, b) => b.rawDate - a.rawDate); // Tri par date décroissante
};

// Récupération des informations des activités (infobulles)
let hasWarnedInfoActivite404 = false; // éviter le spam console si 404 récurrent
export const getInfoActivite = async () => {
  try {
    
    // Utiliser l'endpoint info-activite qui contient les données
    const response = await api.get('/info-activite');
    
    // Pour info-activite (single type), les données sont dans response.data.data directement
    const data = response.data?.data;
    
    if (!data) { return null; }
    
    // logs nettoyés
    
    // Retourner directement les données pour utilisation dans les tooltips
    return data;
  } catch (error) {
    const status = error?.response?.status;
    if (status === 404) { return null; }
    console.error('❌ Erreur lors de la récupération des infos activités:', error);
    return null;
  }
};

const buildFestivalPicantinsPageParams = () => {
  const params = new URLSearchParams();
  params.append('fields[0]', 'Titre');
  params.append('fields[1]', 'Description');
  params.append('populate[Image][fields][0]', 'url');
  return params;
};

const getLaureatPicantinsForFestivalIds = async (festivalIds = []) => {
  const unique = [...new Set(festivalIds.filter(Boolean).map(String))];
  if (!unique.length) return [];
  try {
    const params = buildLaureatPicantinsPageParams();
    params.set('pagination[page]', '1');
    params.set('pagination[pageSize]', String(Math.min(unique.length * 80, MAX_PAGE_SIZE)));
    unique.forEach((id, i) => {
      params.append(`filters[$or][${i}][festival_picantin][id][$eq]`, id);
    });
    const response = await api.get(`/laureat-picantins?${params}`);
    return transformImageUrls(response.data?.data || []);
  } catch (error) {
    console.error('❌ Erreur getLaureatPicantinsForFestivalIds:', error);
    return [];
  }
};

// Récupération paginée Festival Picantins + lauréats/clubs de la page courante
export const getFestivalPicantins = async ({ page = 1, pageSize = 6, search = '' } = {}) => {
  const empty = { items: [], pagination: emptyActivitesPagination(page, pageSize) };
  try {
    const { data, pagination } = await fetchStrapiSinglePage(
      '/festival-picantins',
      () => {
        const params = buildFestivalPicantinsPageParams();
        const term = (search || '').trim();
        if (term) {
          params.append('filters[Titre][$containsi]', term);
        }
        return params;
      },
      { page, pageSize }
    );
    const festivals = transformImageUrls(data);
    const festivalIds = festivals.map((f) => f.id).filter(Boolean);
    const laureatData = await getLaureatPicantinsForFestivalIds(festivalIds);
    const clubIds = [];
    laureatData.forEach((l) => {
      const club = l?.AUnClub;
      const clubId = club?.documentId || club?.id;
      if (clubId) clubIds.push(clubId);
    });
    const clubsData = clubIds.length ? await getClubsByIds(clubIds) : [];
    const items = transformFestivalPicantins(festivals, laureatData, clubsData);
    return { items, pagination };
  } catch (error) {
    console.error('❌ Erreur lors de la récupération des Festival Picantins:', error);
    return empty;
  }
};

// Récupération des clubs avec leurs images (pour association avec les lauréats)
/** @deprecated Préférer getClubsByIds pour le Festival Picantins */
export const getAllClubsWithImages = async () => getClubsLight();

const buildLaureatPicantinsPageParams = () => {
  const params = new URLSearchParams();
  params.append('fields[0]', 'Annee');
  params.append('fields[1]', 'NomPrenom');
  params.append('populate[Image][fields][0]', 'url');
  params.append('populate[festival_picantin][fields][0]', 'id');
  params.append('populate[AUnClub][fields][0]', 'Nom');
  params.append('populate[AUnClub][fields][1]', 'documentId');
  params.append('sort[0]', 'Annee:desc');
  return params;
};

/** @deprecated Utiliser getFestivalPicantins (inclut les lauréats de la page). */
export const getLaureatPicantins = async () => {
  const { data } = await fetchStrapiSinglePage(
    '/laureat-picantins',
    () => buildLaureatPicantinsPageParams(),
    { page: 1, pageSize: MAX_PAGE_SIZE }
  );
  return transformImageUrls(data);
};

// Nettoie les URLs d'images instables pour éviter les 404/bruit réseau
const normalizeDisplayImageUrl = (url) => {
  if (!url || typeof url !== 'string') return null;
  if (url.includes('via.placeholder.com')) return null;
  if (url.includes('/wp-content/')) return null;
  return url.startsWith('http') ? url : `${MEDIA_BASE_URL}${url}`;
};

// Transformation des données Festival Picantins pour le format attendu par le frontend
export const transformFestivalPicantins = (festivalData, laureatData, clubsData = []) => {
  if (!festivalData || !Array.isArray(festivalData)) return [];

  // logs nettoyés
  
  // Si pas de données de lauréats, retourner vide
  if (!laureatData || laureatData.length === 0) {
    // logs nettoyés
  }

  // Créer un map des clubs par ID pour recherche rapide
  const clubsMap = {};
  if (clubsData && Array.isArray(clubsData)) {
    clubsData.forEach((club) => {
      if (club.id != null) clubsMap[club.id] = club;
      if (club.documentId) clubsMap[club.documentId] = club;
    });
  }

  return festivalData.map(festival => {
    const festivalId = festival.id;
    // logs nettoyés
    
    // Filtrer les lauréats qui appartiennent à ce festival
    const laureats = laureatData
      .filter(laureat => {
        // logs nettoyés
        const isMatch = laureat.festival_picantin?.id === festivalId;
        // logs nettoyés
        return isMatch;
      })
      .map((laureat, laureatIdx) => {
        // Récupérer l'image du lauréat ou celle du club
        let image = null;
        
        // Recherche d'image lauréat (plusieurs structures possibles)
        if (laureat.Image) {
          if (Array.isArray(laureat.Image) && laureat.Image.length > 0) {
            image = laureat.Image[0].url;
            // logs nettoyés
          } else if (laureat.Image.url) {
            image = laureat.Image.url;
            // logs nettoyés
          } else if (laureat.Image.data) {
            if (Array.isArray(laureat.Image.data) && laureat.Image.data.length > 0) {
              const imageData = laureat.Image.data[0].attributes || laureat.Image.data[0];
              image = imageData.url;
              // logs nettoyés
            } else if (laureat.Image.data.attributes) {
              image = laureat.Image.data.attributes.url;
              // logs nettoyés
            }
          }
        }

        // Si pas d'image lauréat, chercher l'image du club
        if (!image && laureat.AUnClub?.id) {
          const club = clubsMap[laureat.AUnClub.id];
          // logs nettoyés
          
          // Vérifier si Image existe dans le club
          if (club?.Image) {
            // logs nettoyés
            
            if (Array.isArray(club.Image) && club.Image.length > 0) {
              image = club.Image[0].url;
              // logs nettoyés
            } else if (club.Image?.url) {
              image = club.Image.url;
              // logs nettoyés
            } else if (club.Image.data) {
              // logs nettoyés
              if (Array.isArray(club.Image.data) && club.Image.data.length > 0) {
                const imageData = club.Image.data[0].attributes || club.Image.data[0];
                image = imageData.url;
                // logs nettoyés
              } else if (club.Image.data.url) {
                image = club.Image.data.url;
                // logs nettoyés
              }
            } else {
              // logs nettoyés
            }
          } else {
            // logs nettoyés
          }
        }

        // logs nettoyés

        // Transformer l'URL en URL complète si elle existe
        const fullImageUrl = normalizeDisplayImageUrl(image);

        // Récupérer le nom du club depuis la relation AUnClub
        const clubName = laureat.AUnClub?.Nom || laureat.Club || laureat.club || null;
        const laureatName = laureat.NomPrenom || laureat.nom || 'Lauréat non renseigné';
        const laureatYear = Number(laureat.Annee || laureat.annee || 0) || 0;

        return {
          id: laureat.id || `${festivalId}-${laureatIdx}`,
          nom: laureatName,
          annee: laureatYear,
          // Ne définir le club que s'il existe vraiment
          ...(clubName && { club: clubName }),
          // Ne définir l'image que si on en a trouvé une (lauréat ou club)
          ...(fullImageUrl && { image: fullImageUrl })
        };
      })
      .sort((a, b) => b.annee - a.annee); // Trier par année décroissante

    // Récupérer l'image du festival
    let festivalImage = null;
    // logs nettoyés
    
    // Gérer différentes structures d'image possibles
    if (festival.Image) {
      // Si c'est un tableau d'images
      if (Array.isArray(festival.Image) && festival.Image.length > 0) {
        festivalImage = festival.Image[0].url;
        // logs nettoyés
      }
      // Si c'est un objet image direct
      else if (festival.Image.url) {
        festivalImage = festival.Image.url;
        // logs nettoyés
      }
      // Si c'est dans une structure data
      else if (festival.Image.data) {
        if (Array.isArray(festival.Image.data) && festival.Image.data.length > 0) {
          const imageData = festival.Image.data[0].attributes || festival.Image.data[0];
          festivalImage = imageData.url;
          // logs nettoyés
        } else if (festival.Image.data.attributes) {
          festivalImage = festival.Image.data.attributes.url;
          // logs nettoyés
        }
      }
      else {
        // logs nettoyés
      }
    } else {
      // logs nettoyés
    }

    // Transformer l'URL du festival en URL complète
    const fullFestivalImageUrl = normalizeDisplayImageUrl(festivalImage);

    const transformedFestival = {
      id: `strapi-festival-${festival.id}`,
      image: fullFestivalImageUrl,
      title: festival.Titre || 'Festival des Picantins',
      category: "Festival des Picantins",
      date: festival.createdAt ? new Date(festival.createdAt).toLocaleDateString('fr-FR') : new Date().toLocaleDateString('fr-FR'),
      author: "Comité OSARC",
      excerpt: cleanAndTruncateHTML(festival.Description || '', 3),
      description: festival.Description || '',
      rawDate: festival.createdAt ? new Date(festival.createdAt) : new Date(),
      laureats: laureats
    };

    // logs nettoyés
    return transformedFestival;
  });
};

// Fonction utilitaire pour nettoyer et tronquer le HTML
const cleanAndTruncateHTML = (htmlString, maxLines = 3) => {
  if (!htmlString) return '';
  
  // Créer un élément temporaire pour extraire le texte
  const temp = document.createElement('div');
  temp.innerHTML = htmlString;
  const text = temp.textContent || temp.innerText || '';
  
  // Découper en mots et limiter
  const words = text.split(' ');
  const wordsPerLine = 15; // Approximation
  const maxWords = maxLines * wordsPerLine;
  
  if (words.length > maxWords) {
    return words.slice(0, maxWords).join(' ') + '...';
  }
  
  return text;
};

/**
 * =====================================================================
 * NOUVELLES FONCTIONS API POUR LA STRUCTURE SONDAGE SIMPLIFIÉE
 * =====================================================================
 * 
 * Structure : Sondage (avec clubs) + Vote (IP + club + sondage)
 * Processus : 1 vote par IP par 24h pour un club dans un sondage
 */

/**
 * Récupère le sondage actif avec ses clubs participants.
 * Si aucun sondage actif, retourne le dernier sondage terminé (date de fin la plus proche d'aujourd'hui).
 * @returns {Promise<Object>} Sondage actif ou dernier sondage terminé avec ses clubs ou null
 */
export const getSondageActif = async () => {
  try {
    // Première tentative : récupérer tous les sondages avec les clubs ET leurs images
    // Augmenter la limite pour être sûr de récupérer tous les sondages
    const response = await api.get('/sondages?populate[clubs][populate][0]=Image&pagination[limit]=100&sort=Date_Fin:desc');
    
    if (response.data?.data) {
      // Filtrer côté client pour trouver le sondage actif
      const sondageActif = response.data.data.find(sondage => 
        sondage.Statut === 'Actif'
      );
      
      if (sondageActif) {
        return sondageActif;
      } else {
        // Trouver le dernier sondage terminé (date de fin la plus proche d'aujourd'hui)
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        const sondagesTermines = response.data.data
          .filter(sondage => {
            const statut = sondage.Statut || sondage.statut || '';
            const dateFin = sondage.Date_Fin || sondage.DateFin || sondage.date_fin;
            
            // Sondage terminé si statut = "Terminé" ou si date de fin < aujourd'hui
            if (statut.toLowerCase() === 'terminé' || statut.toLowerCase() === 'termine') {
              return true;
            }
            
            if (dateFin) {
              const dateFinObj = new Date(dateFin);
              dateFinObj.setHours(0, 0, 0, 0);
              const isFinished = dateFinObj < today;
              return isFinished;
            }
            
            return false;
          })
          .map(sondage => {
            const dateFin = sondage.Date_Fin || sondage.DateFin || sondage.date_fin;
            return {
              sondage,
              dateFin: dateFin ? new Date(dateFin).getTime() : 0
            };
          })
          .sort((a, b) => b.dateFin - a.dateFin); // Trier par date de fin décroissante
        
        if (sondagesTermines.length > 0) {
          const dernierSondage = sondagesTermines[0].sondage;
          return dernierSondage;
        }
        
        return null;
      }
    }
    
    return null;
  } catch (error) {
    console.error('❌ Erreur lors de la récupération du sondage actif:', error);
    
    try {
      // Tentative sans populate mais avec tri et limite augmentée
      const simpleResponse = await api.get('/sondages?pagination[limit]=100&sort=Date_Fin:desc');
      
      if (simpleResponse.data?.data) {
        const sondageActif = simpleResponse.data.data.find(sondage => 
          sondage.Statut === 'Actif'
        );
        
        if (sondageActif) {
          try {
            // Utiliser le documentId au lieu de l'ID numérique pour Strapi v5
            const clubResponse = await api.get(`/sondages/${sondageActif.documentId}?populate[clubs][populate][0]=Image`);
            
            if (clubResponse.data?.data?.clubs) {
              return clubResponse.data.data;
            }
          } catch (clubError) {
            // Erreur silencieuse
          }
          
          // Retourner le sondage même sans clubs
          return sondageActif;
        } else {
          // Aucun sondage actif, chercher le dernier terminé
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          
          const sondagesTermines = simpleResponse.data.data
            .filter(sondage => {
              const statut = sondage.Statut || sondage.statut || '';
              const dateFin = sondage.Date_Fin || sondage.DateFin || sondage.date_fin;
              
              if (statut.toLowerCase() === 'terminé' || statut.toLowerCase() === 'termine') {
                return true;
              }
              
              if (dateFin) {
                const dateFinObj = new Date(dateFin);
                dateFinObj.setHours(0, 0, 0, 0);
                const isFinished = dateFinObj < today;
                return isFinished;
              }
              
              return false;
            })
            .map(sondage => {
              const dateFin = sondage.Date_Fin || sondage.DateFin || sondage.date_fin;
              return {
                sondage,
                dateFin: dateFin ? new Date(dateFin).getTime() : 0
              };
            })
            .sort((a, b) => b.dateFin - a.dateFin);
          
          if (sondagesTermines.length > 0) {
            const dernierSondage = sondagesTermines[0].sondage;
            
            // Récupérer les clubs pour ce sondage
            try {
              const clubResponse = await api.get(`/sondages/${dernierSondage.documentId}?populate[clubs][populate][0]=Image`);
              if (clubResponse.data?.data?.clubs) {
                return clubResponse.data.data;
              }
            } catch (clubError) {
              // Erreur silencieuse
            }
            
            return dernierSondage;
          }
        }
      }
      
      return null;
    } catch (simpleError) {
      console.error('❌ Erreur même en mode simplifié:', simpleError);
      return null;
    }
  }
};

/**
 * Récupère le dernier sondage terminé (date de fin la plus récente parmi les terminés).
 * Utilisé pour afficher le gagnant du sondage précédent quand un sondage "à venir" est affiché.
 * @returns {Promise<Object|null>} Dernier sondage terminé avec clubs ou null
 */
export const getDernierSondageTermine = async () => {
  try {
    const response = await api.get('/sondages?populate[clubs][populate][0]=Image&pagination[limit]=100&sort=Date_Fin:desc');
    if (!response.data?.data?.length) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const sondagesTermines = response.data.data
      .filter(sondage => {
        const statut = sondage.Statut || sondage.statut || '';
        const dateFin = sondage.Date_Fin || sondage.DateFin || sondage.date_fin;
        if (statut.toLowerCase() === 'terminé' || statut.toLowerCase() === 'termine') return true;
        if (dateFin) {
          const dateFinObj = new Date(dateFin);
          dateFinObj.setHours(0, 0, 0, 0);
          return dateFinObj < today;
        }
        return false;
      })
      .map(sondage => {
        const dateFin = sondage.Date_Fin || sondage.DateFin || sondage.date_fin;
        return { sondage, dateFin: dateFin ? new Date(dateFin).getTime() : 0 };
      })
      .sort((a, b) => b.dateFin - a.dateFin);
    if (sondagesTermines.length > 0) return sondagesTermines[0].sondage;
    return null;
  } catch (error) {
    console.error('❌ Erreur getDernierSondageTermine:', error);
    return null;
  }
};

/**
 * Soumet un vote pour un club dans un sondage
 * @param {string} sondageDocumentId - documentId du sondage
 * @param {string} clubDocumentId - documentId du club voté
 * @param {string} captchaToken - Token Cloudflare Turnstile
 * @returns {Promise<Object>} Résultat du vote
 */
export const submitVoteForClub = async (sondageDocumentId, clubDocumentId, captchaToken, ticket) => {
  try {
    const voteData = {
      data: {
        DateReponse: new Date().toISOString(),
        sondage: { connect: [sondageDocumentId] },
        club: { connect: [clubDocumentId] },
        captchaToken,
        ticket
      }
    };
    // withCredentials : permet l'envoi/réception du cookie votant anonyme (couche 3).
    const response = await api.post('/votes', voteData, { withCredentials: true });
    if (response.data?.data) return { success: true, data: response.data.data };
    return { success: false, error: 'Erreur lors de l\'enregistrement du vote' };
  } catch (error) {
    console.error('❌ Erreur lors de la soumission du vote:', error);
    // Remonter le message du backend (ex. ticket expiré) si disponible
    const backendMsg = error.response?.data?.error?.message || error.message;
    return { success: false, error: backendMsg };
  }
};

/**
 * Récupère un ticket de vote signé (HMAC, usage unique, TTL court) requis par
 * le backend avant d'accepter un vote (couche 3 anti-rotation/anti-bot).
 * withCredentials pour que le cookie votant anonyme soit posé/renvoyé.
 * @param {string} sondageDocumentId - documentId du sondage
 * @returns {Promise<string|null>} le ticket, ou null en cas d'échec
 */
export const getVoteTicket = async (sondageDocumentId) => {
  if (!sondageDocumentId) return null;
  try {
    const response = await api.get(`/votes/ticket/${sondageDocumentId}`, { withCredentials: true });
    return response.data?.ticket || null;
  } catch (error) {
    console.error('❌ Erreur lors de la récupération du ticket de vote:', error?.message);
    return null;
  }
};

/**
 * Récupère les résultats de vote pour un sondage
 * @param {number} sondageId - ID du sondage
 * @returns {Promise<Object>} Résultats avec votes et pourcentages
 */
export const getVoteResults = async (sondageDocumentId) => {
  if (!sondageDocumentId) return { results: [], totalVotes: 0 };
  try {
    const response = await api.get(`/votes/results/${sondageDocumentId}`);
    return response.data;
  } catch (e) {
    return { results: [], totalVotes: 0 };
  }
};

/**
 * Récupère le statut de vote pour un sondage en s'appuyant sur l'IP RÉELLE
 * lue côté serveur (endpoint sécurisé /votes/check). Contrairement à un
 * GET /votes filtré, cet endpoint :
 *  - n'expose pas la liste des votes au public ;
 *  - ignore toute IP envoyée par le client (anti-triche) ;
 *  - reste fiable même si l'utilisateur a vidé son cache / localStorage.
 *
 * @param {string} sondageDocumentId - documentId du sondage
 * @returns {Promise<{hasVoted: boolean, clubNom: string|null, dateReponse: string|null}>}
 */
export const getVoteStatusForSondage = async (sondageDocumentId) => {
  const empty = { hasVoted: false, clubNom: null, dateReponse: null };
  if (!sondageDocumentId) return empty;
  try {
    // Le segment d'IP de l'URL est ignoré par le backend (il lit l'IP réelle de la requête).
    // withCredentials : envoie le cookie votant anonyme pour une détection cohérente.
    const response = await api.get(`/votes/check/client/${sondageDocumentId}`, { withCredentials: true });
    const data = response.data || {};
    if (!data.hasVoted || !data.vote) return empty;

    const voteData = data.vote.attributes || data.vote;
    const club = voteData.club?.data || voteData.club || null;
    const clubAttrs = club?.attributes || club || {};
    const clubNom = clubAttrs.Nom || clubAttrs.nom || null;
    const dateReponse = voteData.DateReponse || null;

    return { hasVoted: true, clubNom, dateReponse };
  } catch (error) {
    console.debug('getVoteStatusForSondage échec:', error?.message);
    return empty;
  }
};

/**
 * =====================================================================
 * FONCTION DE DIAGNOSTIC POUR LES SONDAGES
 * =====================================================================
 */

/**
 * Diagnostique les sondages pour comprendre pourquoi aucun n'est trouvé
 * @returns {Promise<Object>} Informations de diagnostic
 */
export const diagnoseSondages = async () => {
  try {
    // 1. Récupérer TOUS les sondages (sans filtre)
    const allSondagesResponse = await api.get(`/sondages?populate=*&pagination[page]=1&pagination[pageSize]=${MAX_PAGE_SIZE}`);
    const allSondages = allSondagesResponse.data?.data || [];
    
    if (allSondages.length === 0) {
      return { error: 'Aucun sondage trouvé dans Strapi' };
    }
    
    // 2. Analyser chaque sondage
    const analysis = allSondages.map(sondage => {
      const titre = sondage.Titre || sondage.titre || 'Sans titre';
      const statut = sondage.Statut || sondage.statut || 'Statut manquant';
      const isPublished = !!sondage.publishedAt;
      const clubs = sondage.clubs?.data || sondage.clubs || [];
      
      return {
        id: sondage.id,
        titre,
        statut,
        isPublished,
        publishedAt: sondage.publishedAt,
        clubsCount: clubs.length,
        clubs: clubs.map(club => club.Nom || club.nom || 'Club sans nom'),
        isValidForActive: statut === 'Actif' && isPublished && clubs.length > 0
      };
    });
    
    // 4. Rechercher les sondages actifs
    const activeSondages = analysis.filter(s => s.isValidForActive);
    
    if (activeSondages.length === 0) {
      return { 
        error: 'Aucun sondage valide trouvé',
        allSondages: analysis,
        suggestions: [
          'Créer un nouveau sondage',
          'Mettre le statut sur "Actif"',
          'Publier le sondage',
          'Associer des clubs au sondage'
        ]
      };
    }
    
    return {
      success: true,
      allSondages: analysis,
      activeSondages,
      totalCount: allSondages.length
    };
    
  } catch (error) {
    console.error('❌ DIAGNOSTIC - Erreur lors du diagnostic:', error);
    return { error: error.message };
  }
};

/**
 * Recherche globale sur plusieurs entités Strapi (actualités, événements, clubs, livres, partenaires, activités hors osarc mag)
 * @param {string} query - Texte recherché
 * @returns {Object} Résultats groupés par type (5 max par type)
 */
/**
 * Envoie un email de contact via l'API Strapi
 * @param {string} name - Nom de l'expéditeur
 * @param {string} email - Email de l'expéditeur
 * @param {string} subject - Sujet du message
 * @param {string} message - Contenu du message
 * @returns {Promise<Object>} Réponse de l'API
 */
export const sendContactEmail = async (name, email, subject, message) => {
  try {
    // Timeout augmenté à 30 secondes pour l'envoi d'email (peut prendre du temps avec SMTP)
    const response = await api.post('/contact/send', {
      name,
      email,
      subject,
      message
    }, {
      timeout: 30000 // 30 secondes pour l'envoi d'email
    });
    return response.data;
  } catch (error) {
    console.error('Erreur lors de l\'envoi de l\'email de contact:', error);
    throw error;
  }
};

const appendOrContainsFilter = (params, orIndex, fieldPath, value) => {
  const parts = fieldPath.split('.');
  let key = `filters[$or][${orIndex}]`;
  parts.forEach((part) => {
    key += `[${part}]`;
  });
  params.append(`${key}[$containsi]`, value);
};

const searchCollection = async (endpoint, { q, limit, orFilters, populateFn }) => {
  try {
    const params = new URLSearchParams();
    params.append('pagination[page]', '1');
    params.append('pagination[pageSize]', String(limit));
    orFilters.forEach((filter, i) => {
      appendOrContainsFilter(params, i, filter.field, q);
    });
    if (populateFn) populateFn(params);
    const response = await api.get(`${endpoint}?${params}`);
    const data = response.data?.data || [];
    return transformImageUrls(data);
  } catch {
    return [];
  }
};

/** Recherche globale navbar — requêtes ciblées (alias searchAllEntities). */
export const searchAllEntities = async (query) => {
  const empty = {
    actualites: [],
    evenements: [],
    clubs: [],
    livres: [],
    partenaires: [],
    activites: [],
  };
  if (!query || query.length < 2) return empty;

  const limit = 5;
  const q = query.trim();

  try {
    const actualitesP = searchCollection('/actualites', {
      q,
      limit,
      orFilters: [
        { field: 'Titre' },
        { field: 'Sport.Designation' },
      ],
      populateFn: (params) => {
        params.append('fields[0]', 'Titre');
        params.append('fields[1]', 'DatePublication');
        params.append('populate[Images][fields][0]', 'url');
        params.append('populate[Sport][fields][0]', 'Designation');
      },
    });

    const evenementsP = searchCollection('/evenements', {
      q,
      limit,
      orFilters: [
        { field: 'Titre' },
        { field: 'Ville' },
        { field: 'Emplacement' },
      ],
      populateFn: (params) => {
        params.append('fields[0]', 'Titre');
        params.append('fields[1]', 'Date_debut');
        appendEvenementListPopulate(params);
      },
    });

    const clubsP = searchCollection('/clubs', {
      q,
      limit,
      orFilters: [
        { field: 'Nom' },
        { field: 'Ville' },
        { field: 'Sport.Designation' },
        { field: 'EtiquettesClub.Intitule' },
        { field: 'EtiquettesClub.Description' },
      ],
      populateFn: appendClubListPopulate,
    });

    const livresP = searchCollection('/livres', {
      q,
      limit,
      orFilters: [
        { field: 'Titre' },
        { field: 'Auteur' },
        { field: 'Editeur' },
      ],
      populateFn: (params) => {
        params.append('fields[0]', 'Titre');
        params.append('populate[Image][fields][0]', 'url');
      },
    });

    const partenairesP = searchCollection('/partenaires', {
      q,
      limit,
      orFilters: [{ field: 'Nom' }, { field: 'Ville' }],
      populateFn: (params) => {
        params.append('fields[0]', 'Nom');
        params.append('populate[Logo][fields][0]', 'url');
      },
    });

    const activiteSearch = (endpoint, titleField = 'Titre') =>
      searchCollection(endpoint, {
        q,
        limit,
        orFilters: [{ field: titleField }],
        populateFn: (params) => params.append('fields[0]', titleField),
      });

    const [actualites, evenements, clubs, livres, partenaires, cdm, festival, trophee, label5a] =
      await Promise.all([
        actualitesP,
        evenementsP,
        clubsP,
        livresP,
        partenairesP,
        activiteSearch('/club-du-mois-decathlons', 'MoisAnnee'),
        activiteSearch('/festival-picantins'),
        activiteSearch('/trophee-inclus-sports'),
        activiteSearch('/label-5-as'),
      ]);

    const activites = [...cdm, ...festival, ...trophee, ...label5a].slice(0, limit);

    return { actualites, evenements, clubs, livres, partenaires, activites };
  } catch (error) {
    console.error('Erreur searchAllEntities:', error);
    return empty;
  }
};

export default api; 